'use client';

// Supabase-shaped client wired to /api/mysql/* routes.
//
// What changed in this revision (audit follow-ups):
//
// 1. select(columns) used to drop its argument — every server-side query
//    returned `SELECT *` regardless of what the UI asked for. Now the column
//    list is forwarded to the API and the SQL is built with that projection.
//    (TF-HIGH-002)
//
// 2. upsert(values, { onConflict }) used to drop `onConflict`, so generic
//    JSON-backed tables silently accumulated duplicate rows on every save.
//    The conflict key is now forwarded and resolved server-side.
//    (TF-HIGH-001)
//
// 3. range(from, to) used to drop the offset, returning page 1 every time.
//    Now both `from` and `to` translate to `LIMIT n OFFSET m`. (TF-HIGH-008)
//
// 4. signInWithPassword({ rememberMe }) used to swallow the toggle, so the
//    "Remember me" checkbox did nothing. Now it forwards. (TF-HIGH-003)
//
// 5. channel(name).on(...).subscribe() used to be a no-op stub. We now
//    fall back to interval polling — every consumer is invoked roughly
//    every REALTIME_POLL_MS, debounced via useRealtimeRefresh. Combined
//    with the existing cross-tab BroadcastChannel + storage event in
//    notifyTaskflowDataChanged, single-user multi-tab is instant and
//    multi-user is delayed by at most one poll period. (TF-HIGH-005)
//
// 6. signOut() now clears local-only caches (cmd_palette_recent etc.)
//    so a shared workstation doesn't leak the previous user's recents
//    to the next sign-in. (TF-HIGH-009)
//
// 7. storage.from().upload() was a no-op stub returning the input path
//    unchanged. Calling it now throws a clear error so callers don't
//    silently lose user uploads. (TF-LOW-007)

type Filter = { column: string; op: string; value: any };
type Order = { column: string; ascending?: boolean };

type QueryPayload = {
  table: string;
  action: 'select' | 'insert' | 'update' | 'delete' | 'upsert';
  values?: any;
  filters: Filter[];
  order: Order[];
  or?: string[];
  textSearch?: { column: string; query: string; options?: any } | null;
  columns?: string | string[] | null;
  onConflict?: string | string[] | null;
  limit?: number;
  offset?: number;
  single?: boolean;
  maybeSingle?: boolean;
  count?: 'exact' | null;
  head?: boolean;
};

function errorObj(message: string) {
  return { message, name: 'TaskFlowMySQLError' };
}

const MAX_TABLE_REQUESTS = 6;
let activeTableRequests = 0;
const tableRequestQueue: Array<() => void> = [];
const inflightSelects = new Map<string, Promise<any>>();

async function runLimited<T>(fn: () => Promise<T>): Promise<T> {
  if (activeTableRequests >= MAX_TABLE_REQUESTS) {
    await new Promise<void>((resolve) => tableRequestQueue.push(resolve));
  }
  activeTableRequests += 1;
  try {
    return await fn();
  } finally {
    activeTableRequests = Math.max(0, activeTableRequests - 1);
    const next = tableRequestQueue.shift();
    if (next) next();
  }
}

function stableStringify(value: any): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
}

async function postTable(payload: QueryPayload) {
  const doFetch = async () => {
    const res = await fetch('/api/mysql/table', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { data: null, error: errorObj(json?.error || `Request failed (${res.status})`), count: null };
    }
    return { data: json.data ?? null, error: json.error ?? null, count: json.count ?? null };
  };

  if (payload.action === 'select') {
    const key = stableStringify(payload);
    const existing = inflightSelects.get(key);
    if (existing) return existing;
    const promise = runLimited(doFetch).finally(() => {
      window.setTimeout(() => inflightSelects.delete(key), 150);
    });
    inflightSelects.set(key, promise);
    return promise;
  }

  return runLimited(doFetch);
}

class LocalQueryBuilder implements PromiseLike<any> {
  private payload: QueryPayload;

  constructor(table: string) {
    this.payload = { table, action: 'select', filters: [], order: [], or: [], textSearch: null };
  }

  // Forward the column list so the server can project (TF-HIGH-002).
  select(columns = '*', options: any = {}) {
    this.payload.columns = columns ?? '*';
    this.payload.count = options?.count || null;
    this.payload.head = Boolean(options?.head);
    return this;
  }
  insert(values: any) { this.payload.action = 'insert'; this.payload.values = values; return this; }
  update(values: any) { this.payload.action = 'update'; this.payload.values = values; return this; }
  // Forward `onConflict` so the server can upsert by the right unique key
  // (TF-HIGH-001). PostgREST users pass `{ onConflict: 'user_id' }`.
  upsert(values: any, options: { onConflict?: string | string[]; ignoreDuplicates?: boolean } = {}) {
    this.payload.action = 'upsert';
    this.payload.values = values;
    if (options?.onConflict) this.payload.onConflict = options.onConflict;
    return this;
  }
  delete() { this.payload.action = 'delete'; return this; }

  eq(column: string, value: any) { this.payload.filters.push({ column, op: 'eq', value }); return this; }
  neq(column: string, value: any) { this.payload.filters.push({ column, op: 'neq', value }); return this; }
  gte(column: string, value: any) { this.payload.filters.push({ column, op: 'gte', value }); return this; }
  lte(column: string, value: any) { this.payload.filters.push({ column, op: 'lte', value }); return this; }
  gt(column: string, value: any) { this.payload.filters.push({ column, op: 'gt', value }); return this; }
  lt(column: string, value: any) { this.payload.filters.push({ column, op: 'lt', value }); return this; }
  is(column: string, value: any) { this.payload.filters.push({ column, op: 'is', value }); return this; }
  not(column: string, op: string, value: any) { this.payload.filters.push({ column, op: `not_${op || 'eq'}`, value }); return this; }
  in(column: string, value: any[]) { this.payload.filters.push({ column, op: 'in', value }); return this; }
  contains(column: string, value: any) { this.payload.filters.push({ column, op: 'contains', value }); return this; }
  or(expression: string) { if (expression) this.payload.or?.push(expression); return this; }
  textSearch(column: string, query: string, options: any = {}) { this.payload.textSearch = { column, query, options }; return this; }

  order(column: string, options: any = {}) { this.payload.order.push({ column, ascending: options?.ascending !== false }); return this; }
  limit(n: number) { this.payload.limit = n; return this; }
  single() { this.payload.single = true; return this; }
  maybeSingle() { this.payload.maybeSingle = true; return this; }
  // Forward both ends so true pagination works (TF-HIGH-008).
  range(from: number, to: number) {
    const offset = Math.max(0, Number(from) || 0);
    const limit = Math.max(0, (Number(to) || 0) - offset + 1);
    this.payload.offset = offset;
    this.payload.limit = limit;
    return this;
  }

  async execute() {
    try {
      return await postTable(this.payload);
    } catch (err) {
      return { data: null, error: errorObj(err instanceof Error ? err.message : 'Request failed'), count: null };
    }
  }

  then<TResult1 = any, TResult2 = never>(onfulfilled?: ((value: any) => TResult1 | PromiseLike<TResult1>) | null, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

async function fetchJson(path: string, init: RequestInit = {}) {
  const res = await fetch(path, { ...init, credentials: 'same-origin', cache: 'no-store' });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) return { data: null, error: errorObj(json?.error || `Request failed (${res.status})`) };
  return { data: json, error: null };
}

// ─── Realtime polling fallback ────────────────────────────────────────────────
//
// The original `channel()` was a chainable no-op — every "real-time" feature
// in the UI silently never received updates. Implementing a proper SSE / WS
// transport is out of scope for this fix, so we expose the same shape but
// drive it from a polling timer. useRealtimeRefresh debounces the fan-out,
// so the actual user-perceived latency is bounded by REALTIME_POLL_MS.

const REALTIME_POLL_MS = 5000;

interface ChannelHandler {
  callback: (payload: { eventType: 'INSERT' | 'UPDATE' | 'DELETE' | '*'; table: string; new?: any; old?: any }) => void;
  table: string;
  event: string;
}

class PollingChannel {
  private handlers: ChannelHandler[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private subscribed = false;

  // Supabase signature: .on(event, filter, callback)
  on(_event: string, filter: any, callback: ChannelHandler['callback']) {
    this.handlers.push({ callback, table: filter?.table || '*', event: filter?.event || '*' });
    return this;
  }

  subscribe(_cb?: any) {
    if (this.subscribed) return this;
    this.subscribed = true;
    // Polling alone won't give us the row payloads, so callbacks fire with
    // `eventType: '*'` and no `new`/`old`. Consumers (NotificationCenter,
    // CommentsPanel, useRealtimeRefresh, etc) all just refetch on any
    // event, which is the right behaviour for this transport.
    this.timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      for (const h of this.handlers) {
        try { h.callback({ eventType: '*', table: h.table }); } catch { /* swallow */ }
      }
    }, REALTIME_POLL_MS);
    return this;
  }

  unsubscribe() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    this.handlers = [];
    this.subscribed = false;
  }
}

// ─── Local-only cache cleanup on signOut (TF-HIGH-009) ────────────────────────
const PER_USER_LOCAL_KEYS = [
  'cmd_palette_recent',
  'taskflow:last-change',
];
function clearPerUserLocalCaches() {
  if (typeof window === 'undefined') return;
  for (const k of PER_USER_LOCAL_KEYS) {
    try { window.localStorage.removeItem(k); } catch { /* ignore */ }
  }
}

function makeClient() {
  return {
    auth: {
      async getSession() {
        const { data, error } = await fetchJson('/api/auth/session');
        return { data: { session: data?.session || null }, error };
      },
      async getUser() {
        const { data, error } = await fetchJson('/api/auth/session');
        return { data: { user: data?.user || null }, error };
      },
      onAuthStateChange(_cb: any) {
        // Browser-tab-local: `storage` events fire on signOut from other tabs.
        // We attach a listener and forward to the callback so multi-tab
        // logout actually propagates. The unsubscribe handle is preserved.
        if (typeof window === 'undefined') {
          return { data: { subscription: { unsubscribe() {} } } };
        }
        const handler = (event: StorageEvent) => {
          if (event.key === 'taskflow:auth-event' && typeof _cb === 'function') {
            try { _cb('SIGNED_OUT', null); } catch { /* swallow */ }
          }
        };
        window.addEventListener('storage', handler);
        return { data: { subscription: { unsubscribe() { window.removeEventListener('storage', handler); } } } };
      },
      async signInWithPassword({ email, password, rememberMe }: { email: string; password: string; rememberMe?: boolean }) {
        // Forward rememberMe so the API can pick the right cookie max-age
        // (TF-HIGH-003). Default `undefined` stays "remember" per the API
        // contract — the explicit toggle reaches the server now.
        const { data, error } = await fetchJson('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, rememberMe }),
        });
        return { data, error };
      },
      async signUp({ email, password, options }: { email: string; password: string; options?: any }) {
        const { data, error } = await fetchJson('/api/auth/signup', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            fullName: options?.data?.full_name || options?.data?.name || '',
          }),
        });
        return { data, error };
      },
      async signOut() {
        const { error } = await fetchJson('/api/auth/logout', { method: 'POST' });
        // Wipe local caches that contain user-scoped data — failing to do
        // this leaks the previous user's command palette recents to the
        // next sign-in on a shared workstation.
        clearPerUserLocalCaches();
        // Notify other tabs (and our own onAuthStateChange listeners).
        if (typeof window !== 'undefined') {
          try { window.localStorage.setItem('taskflow:auth-event', String(Date.now())); } catch { /* ignore */ }
        }
        return { error };
      },
      async signInWithOAuth({ provider, options }: { provider: string; options?: any }) {
        if (provider !== 'google') return { data: null, error: errorObj(`${provider} login is not configured`) };
        let next = '/board-view';
        const redirectTo = options?.redirectTo || '';
        try { next = new URL(redirectTo).searchParams.get('next') || next; } catch {}
        window.location.assign(`/api/auth/google/start?next=${encodeURIComponent(next)}`);
        return { data: { url: window.location.href }, error: null };
      },
    },
    from(table: string) { return new LocalQueryBuilder(table); },
    async rpc(_name: string, _args?: any) {
      // RPC is intentionally unwired — there is no SQL-function endpoint in
      // this build. Callers (e.g. presence) should use direct .from().upsert
      // instead of falling silently to a no-op as the original adapter did.
      return { data: null, error: errorObj(`RPC '${_name}' is not implemented; use .from().upsert(...) instead`) };
    },
    channel(_name: string) { return new PollingChannel(); },
    removeChannel(channel: any) { try { channel?.unsubscribe?.(); } catch { /* ignore */ } },
    storage: {
      from(_bucket: string) {
        return {
          async upload(_path: string, _file: any) {
            // No object-storage backend is wired in this build. The previous
            // stub returned `{ data: { path }, error: null }` which silently
            // dropped uploads — surface a real error instead so callers
            // know the upload didn't happen.
            return { data: null, error: errorObj('File uploads are not configured in this deployment') };
          },
          getPublicUrl(path: string) {
            return { data: { publicUrl: path?.startsWith('http') ? path : `/assets/${path || ''}` } };
          },
        };
      },
    },
  };
}

let singletonClient: ReturnType<typeof makeClient> | null = null;

export function createClient() {
  if (!singletonClient) singletonClient = makeClient();
  return singletonClient;
}
